'use client'

import { PropsWithChildren } from 'react'
import { ApolloLink, HttpLink } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import {
  ApolloNextAppProvider,
  ApolloClient as NextSSRApolloClient,
  InMemoryCache as NextSSRInMemoryCache,
  SSRMultipartLink,
} from '@apollo/experimental-nextjs-app-support'
import { ClientTokenResult, OnErrorErrors } from '../create'

export type SsrClientOptions = {
  clientName?: string
  uri?: string
  uriPublic?: string
  uriProxy?: string
  'x-forwarded-for'?: string
  getHeaders?: () => Promise<object>
  getClientToken?: () => Promise<ClientTokenResult>
  onError?: (errors: OnErrorErrors) => void
  connectToDevTools?: boolean
}

const createClientDefaultOptions = {
  uri: undefined,
}

export const getMakeClient = (options: SsrClientOptions = createClientDefaultOptions) => {
  // have a function to create a client for you
  const makeClient = function () {
    const httpLink = new HttpLink({
      // this needs to be an absolute url, as relative urls cannot be used in SSR
      uri: options.uri,
      // you can disable result caching here if you want to
      // (this does not work if you are rendering your page with `export const dynamic = "force-static"`)
      fetchOptions: { cache: 'no-store' },
      // you can override the default `fetchOptions` on a per query basis
      // via the `context` property on the options passed as a second argument
      // to an Apollo Client data fetching hook, e.g.:
      // const { data } = useSuspenseQuery(MY_QUERY, { context: { fetchOptions: { cache: "force-cache" }}});
      headers: {
        ...(options['x-forwarded-for'] && {
          'x-forwarded-for': options['x-forwarded-for'],
        }),
      },
    })

    const httpLinkPublic = new HttpLink({
      // this needs to be an absolute url, as relative urls cannot be used in SSR
      uri: options.uriPublic,
      // you can disable result caching here if you want to
      // (this does not work if you are rendering your page with `export const dynamic = "force-static"`)
      fetchOptions: { cache: 'no-store' },
      // you can override the default `fetchOptions` on a per query basis
      // via the `context` property on the options passed as a second argument
      // to an Apollo Client data fetching hook, e.g.:
      // const { data } = useSuspenseQuery(MY_QUERY, { context: { fetchOptions: { cache: "force-cache" }}});
      headers: {
        ...(options['x-forwarded-for'] && {
          'x-forwarded-for': options['x-forwarded-for'],
        }),
      },
    })

    const httpLinkProxy = new HttpLink({
      uri: options.uriProxy,
      fetchOptions: { cache: 'no-store' },
      headers: {
        ...(options['x-forwarded-for'] && {
          'x-forwarded-for': options['x-forwarded-for'],
        }),
      },
    })

    const linkSplit = ApolloLink.split(
      (operation) => operation.getContext().clientName === 'public',
      httpLinkPublic, // <= apollo will send to this if clientName is "public"
      // httpLink // <= otherwise will send to this
      ApolloLink.split(
        (operation) => operation.getContext().clientName === 'proxy',
        httpLinkProxy, // <= apollo will send to this if clientName is "public"
        httpLink // <= otherwise will send to this
      )
    )

    // If getHeaders function exists, use it to populate request headers (for use with auth)
    const headersLink = setContext(async (_, prevContext) => {
      const { accessToken, additionalHeaders, clientName } = prevContext
      const isPublicReq = clientName === 'public'
      const isDirectReq = clientName === 'direct'

      let headers: object | undefined
      let directToken: string | undefined

      if (isPublicReq) {
        headers = undefined
      } else if (isDirectReq && options.getClientToken) {
        const tokenResult = await options.getClientToken()
        directToken = tokenResult.token
      } else if (options.getHeaders) {
        headers = await options.getHeaders()
      }

      return {
        headers: {
          ...prevContext.headers,
          ...(directToken ? { authorization: `Bearer ${directToken}` } : {}),
          ...(accessToken && !isDirectReq ? { authorization: `Bearer ${accessToken}` } : {}),
          ...headers,
          ...generateClientSideHeaders(),
          ...additionalHeaders,
        },
      }
    })

    const ForwardUiConfigExtensionsLink = new ApolloLink((operation, forward) => {
      // Called after server responds
      return forward(operation).map((response) => {
        if (response.data && response.extensions) {
          response.data.uiConfig = response.extensions.uiConfig
        }
        return response
      })
    })

    const errorLink = onError(({ graphQLErrors, networkError, ...rest }) => {
      if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }) =>
          console.log(`[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}`)
        )
      }
      if (networkError) {
        console.log(`[Network error]: ${networkError}`)
      }
      if (options.onError) {
        options.onError({ graphQLErrors, networkError, ...rest })
      }
    })

    return new NextSSRApolloClient({
      name: options.clientName,
      // use the `NextSSRInMemoryCache`, not the normal `InMemoryCache`
      cache: new NextSSRInMemoryCache(),
      link:
        typeof window === 'undefined'
          ? ApolloLink.from([
              // in a SSR environment, if you use multipart features like
              // @defer, you need to decide how to handle these.
              // This strips all interfaces with a `@defer` directive from your queries.
              new SSRMultipartLink({
                stripDefer: true,
              }),
              headersLink,
              errorLink,
              ForwardUiConfigExtensionsLink,
              linkSplit,
            ])
          : ApolloLink.from([headersLink, errorLink, ForwardUiConfigExtensionsLink, linkSplit]),
    })
  }
  return makeClient
}

interface ApolloWrapperProps extends PropsWithChildren<unknown> {
  client: ReturnType<typeof getMakeClient>
}

// you need to create a component to wrap your app in
export function ApolloWrapper({ client, children }: ApolloWrapperProps) {
  return <ApolloNextAppProvider makeClient={client}>{children}</ApolloNextAppProvider>
}

const generateClientSideHeaders = () => {
  const heapSessionId = typeof window !== 'undefined' ? window?.heap?.getSessionId?.() : undefined
  const heapUserId = typeof window !== 'undefined' ? window.heap?.getUserId?.() || window.heap?.userId : undefined
  return {
    ...(heapSessionId && { 'x-heap-session-id': heapSessionId }),
    ...(heapUserId && { 'x-heap-user-id': heapUserId }),
  }
}
